import { useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import { setCookie } from 'cookies-next'
import { useDispatch } from 'react-redux'
import getLinkHref from '@/helpers/getLinkHref'
import { NUMBER_CAR } from '@/constants'
import { fetchCars, fetchFines, fetchStageByStrictSearch } from '@/store/slices/stagesSlice/stagesSlice'
import {
  AreaInfoResponseData,
  StageByStrictSearchResponseData,
} from '@/store/slices/stagesSlice/interfaces'

interface UseCarActionsArgs {
  area: AreaInfoResponseData | null
  areaId: string
  carNumber: string
}

export const useCarActions = ({ area, areaId, carNumber }: UseCarActionsArgs) => {
  const dispatch = useDispatch()
  const router = useRouter()

  const [strictSearchLoading, setStrictSearchLoading] = useState(false)
  const [strictSearchError, setStrictSearchError] = useState('')

  const normalize = useCallback((value: string) => value.replace(/\s/g, ''), [])

  const handleStageButtonClick = useCallback(async () => {
    const number = normalize(carNumber)
    if (number.length <= 2) return

    const notFoundHref = getLinkHref({ type: 'car-number-not-found', data: { id: areaId } })
    const validateCarHref = getLinkHref({ type: 'validate-car', data: { id: areaId, number } })

    await dispatch(
      fetchCars({
        number,
        areaId,
        onSuccess: data => {
          if (data?.length === 0) {
            router.push(notFoundHref)
          } else if (data?.length === 1 && data?.[0].similarity === 1) {
            const stageId = data?.[0].id
            const activeSessionHref = getLinkHref({
              type: 'payment',
              data: { areaId, stageId },
            })
            router.push(activeSessionHref)
          } else {
            router.push(validateCarHref)
          }
          setCookie(NUMBER_CAR, number)
        },
        onError: error => {
          if (error.catched) {
            router.push(validateCarHref)
          }
        },
      }),
    )
  }, [areaId, carNumber, dispatch, normalize, router])

  const handleSubscriptionButtonClick = useCallback(async () => {
    setStrictSearchLoading(true)
    setStrictSearchError('')

    const number = normalize(carNumber)
    if (number.length <= 2) {
      setStrictSearchLoading(false)
      return
    }

    await dispatch(
      fetchStageByStrictSearch({
        number,
        areaId,
        onSuccess: (data: StageByStrictSearchResponseData | null) => {
          const stageId = data?.id ?? null
          const subscriptionsPageHref = getLinkHref({
            type: 'subscriptions',
            data: { areaId: area?.id, number, stageId },
          })
          setCookie(NUMBER_CAR, number)
          router.push(subscriptionsPageHref)
        },
        onError: (messageError?: string) => {
          if (messageError) {
            setStrictSearchError(messageError)
          }
        },
        onFinally: () => {
          setStrictSearchLoading(false)
        },
      }),
    )
  }, [area?.id, areaId, carNumber, dispatch, normalize, router])

  const handleEventButtonClick = useCallback(() => {
    const number = normalize(carNumber)
    if (number.length <= 2) return

    const eventPageHref = getLinkHref({
      type: 'events',
      data: { areaId: area?.id, number },
    })

    router.push(eventPageHref)
    setCookie(NUMBER_CAR, number)
  }, [area?.id, areaId, carNumber, normalize, router])

  const handleFinesButtonClick = useCallback(async ()=> {
    const number = normalize(carNumber)
    if (number.length <= 2) return

    const FinesPageHref = getLinkHref({ type: 'fines-page', data: { areaId, number } })
    // const SelectedFinesPageHref = getLinkHref({ type: 'selected-fines-page', data: { id: areaId, number } }) // todo PRKN-4454
    const notFinesPageHref = getLinkHref({ type: 'no-fines-found', data: { areaId } })

    await dispatch(fetchFines({
      number,
      areaId,
      onSuccess: (data) => {
        if (!data.length) {
          router.push(notFinesPageHref)
          return
        }

        setCookie(NUMBER_CAR, number)
        router.push(FinesPageHref)
      },
      onError: (messageError?: string) => {
        if (messageError) {
          setStrictSearchError(messageError)
        }
      },
    }))
  }, [area?.id, areaId, carNumber, dispatch, normalize, router])

  return {
    strictSearchLoading,
    strictSearchError,
    handleStageButtonClick,
    handleSubscriptionButtonClick,
    handleEventButtonClick,
    handleFinesButtonClick,
  }
}
